import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { client } from '@xinfer/api-client';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  Input,
  Label,
  Separator,
  Skeleton,
  ThemeToggle,
  usePageTitle,
} from '@xinfer/ui';
import { AlertTriangle, TerminalSquare } from 'lucide-react';

/**
 * Sign-in, rendered from what the api says it accepts.
 *
 * Every method here is conditional on `GET /v1/auth/providers`, because none of
 * them exist unconditionally: OAuth providers are configured per deployment,
 * and `dev-login` is not routed at all without `--dev-login`. This component
 * once rendered the dev form unconditionally, which meant the production portal
 * offered exactly one button and it posted to a 404 — while the method that did
 * work had no button at all.
 *
 * The OAuth buttons are links, not fetches: the api answers with a 307 to the
 * provider, and that has to happen in the address bar.
 */

const PROVIDER_LABELS: Record<string, string> = {
  github: 'GitHub',
  google: 'Google',
};

/**
 * Brand marks for the providers we support.
 *
 * Inline rather than from an icon set: `lucide-react` deliberately carries no
 * trademarks, and a generic key glyph on a "Continue with GitHub" button reads
 * as an unfinished page on the one screen where trust matters most.
 */
const PROVIDER_ICONS: Record<string, JSX.Element> = {
  github: (
    <svg viewBox="0 0 16 16" aria-hidden className="size-4" fill="currentColor">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.4 7.4 0 0 1 2-.27c.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
    </svg>
  ),
  google: (
    <svg viewBox="0 0 18 18" aria-hidden className="size-4">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  ),
};

export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  usePageTitle('Sign in');
  const [email, setEmail] = useState('');

  const methods = useQuery({
    queryKey: ['auth-methods'],
    queryFn: () => client.authMethods(),
  });

  const signIn = useMutation({
    mutationFn: (value: string) => client.devLogin(value),
    onSuccess: onSignedIn,
  });

  return (
    <SignInFrame>
      {methods.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      )}

      {/* Failing closed is deliberate: guessing a form here is exactly what
          produced a button that could not work. */}
      {(methods.isError || (!methods.isLoading && !methods.data)) && (
        <Alert variant="danger">
          <AlertTriangle />
          <div>
            <AlertTitle>Sign-in is unavailable</AlertTitle>
            <AlertDescription>
              The control plane is unreachable, so the portal cannot tell which sign-in methods
              this deployment accepts.
            </AlertDescription>
          </div>
        </Alert>
      )}

      {methods.data && <Methods data={methods.data} />}

      {methods.data?.dev_login && (
        <>
          {methods.data.providers.length > 0 && (
            <div className="relative py-1">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs uppercase tracking-wide text-muted-foreground">
                or
              </span>
            </div>
          )}

          <div className="rounded-lg border border-dashed border-warning/50 bg-warning/5 p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <TerminalSquare className="size-4 text-warning" />
              Development sign-in
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              This api was started with <code className="font-mono">--dev-login</code>. Any email
              is accepted, and no password is checked.
            </p>

            <form
              className="mt-3 space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (email) signIn.mutate(email);
              }}
            >
              <Label htmlFor="dev-email" className="sr-only">
                Email
              </Label>
              <Input
                id="dev-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                type="submit"
                variant="secondary"
                className="w-full"
                disabled={!email || signIn.isPending}
              >
                {signIn.isPending ? 'Signing in…' : 'Continue'}
              </Button>
            </form>

            {signIn.isError && <p className="mt-2 text-xs text-destructive">Sign-in failed.</p>}
          </div>
        </>
      )}
    </SignInFrame>
  );
}

function Methods({ data }: { data: { providers: string[]; dev_login: boolean } }) {
  // The api logs this at startup too, but nobody reading the portal sees the
  // log. Saying so beats an empty page that looks like it is still loading.
  if (data.providers.length === 0 && !data.dev_login) {
    return (
      <Alert variant="danger">
        <AlertTriangle />
        <div>
          <AlertTitle>No sign-in method is configured</AlertTitle>
          <AlertDescription>
            Nobody can sign in until one exists. Set <code>XINFER_GITHUB_OAUTH</code> or{' '}
            <code>XINFER_GOOGLE_OAUTH</code> on the api.
          </AlertDescription>
        </div>
      </Alert>
    );
  }

  return (
    <div className="space-y-2">
      {data.providers.map((provider) => (
        <Button key={provider} asChild variant="outline" className="w-full">
          <a href={client.loginUrl(provider)}>
            {PROVIDER_ICONS[provider]}
            Continue with {PROVIDER_LABELS[provider] ?? provider}
          </a>
        </Button>
      ))}
    </div>
  );
}

function SignInFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-muted/30">
      <div className="flex justify-end p-4">
        <ThemeToggle />
      </div>

      <div className="flex flex-1 items-start justify-center px-4 pb-16 pt-[8vh]">
        <div className="w-full max-w-sm">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 flex size-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <svg viewBox="0 0 32 32" aria-hidden className="size-5">
                <path
                  d="M10 10.5 22 21.5 M22 10.5 10 21.5"
                  stroke="currentColor"
                  strokeWidth="3.2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <h1 className="text-lg font-semibold tracking-tight">Sign in to xinfer</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Keys, usage and limits for the inference service.
            </p>
          </div>

          <Card className="space-y-4 p-6 shadow-sm">{children}</Card>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            xinfer stores no passwords. Sign-in is delegated to your identity provider.
          </p>
        </div>
      </div>
    </div>
  );
}
